import { Dispatch, useCallback, useEffect, useReducer } from "react";
import { useTranslation } from "utilities/i18n";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AvailableLicenseFragment,
  useActivateLicenseMutation
} from "generated/graphql";
import { DISMISS, useNotification } from "collections";
import { formatDate } from "routes/Manage/Inventory/View/DeviceDetails";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export enum ActivationStage {
  InputLicenseKeys,
  ReviewLicenseInfo,
  ActivationInitiated,
  ActivationInProgress,
  Completed
}

export enum ActionType {
  AddLicense,
  DeleteLicense,
  ProcessLicenseInfo,
  InitiateActivation,
  WaitForResults,
  Complete
}

export type State = {
  stage: ActivationStage;
  licensesByKey: Record<string, AvailableLicenseFragment[]>;
  tableData: {
    model: string;
    licenseKey: string;
    quantity: string;
    endDate: string;
  }[];
};

export type Action =
  | {
      type: ActionType.AddLicense;
      payload: { licenseKey: string; licenses: AvailableLicenseFragment[] };
    }
  | {
      type: ActionType.DeleteLicense;
      payload: { licenseKey: string };
    }
  | {
      type: ActionType.ProcessLicenseInfo;
      payload?: { licenseKey: string; licenses: AvailableLicenseFragment[] };
    }
  | { type: ActionType.InitiateActivation }
  | { type: ActionType.WaitForResults }
  | { type: ActionType.Complete };

const getUniqueLicenses = (
  licenseInfoByKey: Record<string, AvailableLicenseFragment[]>
) => {
  return Object.values(licenseInfoByKey)
    .flat()
    .filter(
      ({ licenseKey }, i, licenses) =>
        licenses.findIndex(license => licenseKey === license.licenseKey) === i
    );
};

const getTableData = (
  licensesByKey: Record<string, AvailableLicenseFragment[]>
) => {
  const licenses = getUniqueLicenses(licensesByKey);
  const tableData = licenses.flatMap(license =>
    license.models.map(model => ({
      model: model,
      licenseKey: license.licenseKey ?? "",
      quantity: license.quantity?.toString() ?? "",
      endDate: license.endDate ? formatDate(license.endDate, "P") : "-"
    }))
  );
  return tableData;
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.AddLicense: {
      const licensesByKey = {
        ...state.licensesByKey,
        [action.payload.licenseKey]: action.payload.licenses
      };
      return { ...state, licensesByKey };
    }

    case ActionType.DeleteLicense: {
      if (state.stage >= ActivationStage.ActivationInitiated) {
        return { ...state };
      }
      const licensesByKey = { ...state.licensesByKey };
      delete licensesByKey[action.payload.licenseKey];
      const tableData = getTableData(licensesByKey);
      const stage =
        tableData.length === 0 ? ActivationStage.InputLicenseKeys : state.stage;
      return { ...state, licensesByKey, tableData, stage };
    }

    case ActionType.ProcessLicenseInfo: {
      const licensesByKey = action.payload
        ? {
            ...state.licensesByKey,
            [action.payload.licenseKey]: action.payload.licenses
          }
        : { ...state.licensesByKey };
      const tableData = getTableData(licensesByKey);
      const stage = ActivationStage.ReviewLicenseInfo;
      return { ...state, licensesByKey, tableData, stage };
    }

    case ActionType.InitiateActivation: {
      return { ...state, stage: ActivationStage.ActivationInitiated };
    }

    case ActionType.WaitForResults: {
      return { ...state, stage: ActivationStage.ActivationInProgress };
    }

    case ActionType.Complete: {
      return { ...state, stage: ActivationStage.Completed };
    }
  }
};

const initialState: State = {
  stage: ActivationStage.InputLicenseKeys,
  licensesByKey: {},
  tableData: []
};

export const useActivationReducer = (): [State, Dispatch<Action>] => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const userId = useAuth0()?.user?.email ?? "";
  const tenantId = useTenantId();
  const [, activate] = useActivateLicenseMutation();
  const [state, dispatch] = useReducer(reducer, initialState);

  const activateLicenses = useCallback(
    async (licenses: AvailableLicenseFragment[]) => {
      const results = await Promise.all(
        licenses.map(({ licenseKey }) =>
          activate({ tenantId, userId, licenseKey })
        )
      );

      results.forEach(result => {
        if (result.error) {
          addNotification({
            type: "error",
            message: t("LICENSE_KEY_ACTIVATION_ERROR", {
              activationKey: result.operation.variables?.licenseKey ?? ""
            }),
            dismiss: DISMISS.USER_ONLY
          });
        }
      });

      dispatch({ type: ActionType.Complete });
    },
    [t, tenantId, userId, addNotification, activate]
  );

  useEffect(() => {
    if (state.stage === ActivationStage.ActivationInitiated) {
      const licenses = getUniqueLicenses(state.licensesByKey);
      activateLicenses(licenses);
      dispatch({ type: ActionType.WaitForResults });
    }
  }, [state, activateLicenses]);

  return [state, dispatch];
};
